import {
  Step,
  StepLabel,
  Stepper
} from '@mui/material';
import { calculateLocatinVars } from '../../../libs/browser';
import { parseError } from '../../../libs/parser';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Context from '../../../provider';
import IconBenefitsRed from '../../../assets/hkdc/icons/BenefitsRedIcon';
import IconSettingTag from '../../../assets/hkdc/icons/SettingTagIcon';
import LogoFooterComponent from '../../../components/LogoFooterComponent';
import MembershipCardComponent from '../../../components/MembershipCardComponent';
import PageTitleContComponent from '../../../components/PageTitleContComponent';
import React, { useContext, useEffect, useRef, useState } from 'react';
import TierFormGroupComponent from '../../../components/TierFormGroupComponent';
import TierInfoListCheckComponent from '../../../components/TierInfoListCheckComponent';
import TierInfoSubmitComponent from '../../../components/TierInfoSubmitComponent';
import config from '../../../config';
import memberApi from '../../../libs/api/memberApi';
import useIsScrolling from '../../../hooks/useIsScrolling';
import useNotificationsCount from '../../../hooks/useNotificationsCount';
import './index.scss';

const steps = [
  'registration.step-1-title',
  'common.personal-info',
  'registration.step-3-title',
  'registration.step-4-title'
];

export default function WebRegistration() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationVars = calculateLocatinVars(location.pathname);

  const notificationNotReadCount = useNotificationsCount();

  const { init, me, dispatch, appConfig, registrationData } = useContext(Context);

  const containerRef = useRef(null);
  const isScrolling = useIsScrolling(containerRef.current);

  const [membershipsData, setMembershipsData] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [currentTier, setCurrentTier] = useState({});
  const [formFile, setFormFile] = useState(null);
  const [paymentFile, setPaymentFile] = useState(null);
  const [membershipRecord, setMembershipRecord] = useState(null);
  const [pageContentData, setPageContentData] = useState({});

  const fetchData = async () => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.getMemberships();
      setMembershipsData(response.data);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const fetchPageContentData = async () => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const page = await memberApi.getPage({ ruri: `payment-term` });
      setPageContentData(page);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  useEffect(() => {
    setActiveStep(0);
  }, []);

  // This page only allow logged in user
  useEffect(() => {
    console.log('registration page', init, me);

    if (!me) {
      navigate(`${config.basename}login`, { replace: true });
    } else {
      if (me.status === 'new') {
        fetchData();
        fetchPageContentData();

        console.log('registration page me.status === new', registrationData);

        if (registrationData.isSave) {
          setCurrentTier(registrationData.tier);
          registrationData.formFile && setFormFile(registrationData.formFile);
          for (let index = 0; index < registrationData.step; index++) {
            const newCompleted = completed;
            newCompleted[index] = true;
            setCompleted(newCompleted);
          }

          setActiveStep(registrationData.step);
        }

      } else if (me.status === 'activated') {
        navigate(`${config.basename}`, { replace: true });
      }
    }
  }, [init, me, navigate]);

  useEffect(() => {

    if (currentTier && currentTier.type) {
      if (registrationData.isSave && currentTier.type === registrationData.tier.type ) {
        console.log('registrationData.isSave && currentTier.type === registrationData.tier.type', registrationData.data);
        setMembershipRecord(registrationData.data);
      } else {
        getRegisterRecord(currentTier.type);
      }
    }
  }, [currentTier]);

  useEffect(() => {
    containerRef.current.scrollTo(0, 0);
  }, [activeStep]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const getRegisterRecord = async (type) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.getMembershipsRecord(type);
      response.data.preference_language = "en";
      response.data.payment_preference = "back-in";
      setMembershipRecord(response.data);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const saveRegisterRecord = async (record) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.saveMembershipsRecord(record);
      setMembershipRecord(response.data);
      handleComplete();
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const submitRegisterRecord = async (record) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.submitMembershipsRecord(record);

      navigate(`${config.basename}success`, {
        replace: true,
        state: {
          type: 'registration',
          title: 'success.registration-title',
          desc: 'success.registration-desc'
        }
      });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const handleStep = (step) => () => {
    console.log('handleStep completed', completed);

    if (completed[step]) {
      setActiveStep(step);
    }
  };

  const handleComplete = () => {
    const newCompleted = completed;
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    handleNext();
  };

  const getRegistrationStyle = (step) => {
    switch (step) {
      case 0:
        return {
          padding: '2rem 2rem 8.4375rem 2rem',
          gap: '2rem'
        };
      case 1:
        return {
          padding: '0rem 2rem 8.4375rem 2rem',
          gap: '2rem'
        };
      case 2:
        return {
          padding: '2rem 2rem 8.4375rem 2rem',
          gap: '2rem'
        };
      case 3:
        return {
          padding: '2rem 2rem 8.4375rem 2rem',
          gap: '2rem'
        };
      default:
        return {};
    }
  };

  return (
    (
      <div className='web-registration-page'>

        <div ref={containerRef} className="content-container">
          <div className="black-block full-width">
            <PageTitleContComponent
              titleText={t('registration.page-title')}
              titleTextStyle={{
                paddingLeft: '2.5rem'
              }}
              pageTitleIcon={
                <IconSettingTag
                  style={{
                    width: '20.0725rem',
                    height: '20.1rem'
                  }}
                  size="large" />
              }
            />
          </div>
          <div className="black-block full-width step-cont">
            <Stepper nonLinear activeStep={activeStep}>
              {steps.map((label, index) => {
                return (
                  <Step key={index} active={completed[index]}>
                    <StepLabel onClick={handleStep(index)} />
                  </Step>
                );
              })}
            </Stepper>
          </div>

          <div className="full-width">
            <div className="web-max-container registration-content-container"
              style={getRegistrationStyle(activeStep)}
            >
              {
                activeStep === 1 &&
                <div className="tier-show-section" style={{ backgroundColor: `${currentTier.data.color}` }}>
                  <div className="show-text">
                    {t(`tier-name.${currentTier.type}`)}
                  </div>
                </div>
              }
              <div className="content-title-container">
                <IconBenefitsRed
                  className="icon"
                  size="large" />
                <div className="title-text">
                  {t(steps[activeStep])}
                </div>
              </div>
              <div className='tier-card-list-cont'
                style={ activeStep === 0 || activeStep === 1 ? {
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    margin: activeStep === 0 ? '0 4rem' : undefined
                  } : {
                    flexDirection: 'column',
                    margin: activeStep === 3 ? '0 23.40625rem' : undefined
                  }
                 }
                >
                {
                  activeStep === 0 && membershipsData && membershipsData.length > 0 &&
                  membershipsData.map((membership, index) => {
                    return (
                      <MembershipCardComponent
                        key={index}
                        item={membership}
                        changeTier={(type) => {
                          if (!currentTier.type || type !== currentTier.type) {
                            setCurrentTier({ type: type, data: membership });
                          }
                          handleComplete();
                        }}
                      />
                    );}
                  )
                }
                {
                  activeStep === 1 &&
                  <>
                    <div className="compulsory-text">{t('registration.step-2-remark')}</div>
                    <TierFormGroupComponent
                      membershipRecord={membershipRecord}
                      tierType={currentTier.type}
                      countryCode={ appConfig.mobile_country_codes[0] }
                      fileInfo={formFile}
                      onNext= {(record, file) => {
                        setFormFile(file);
                        saveRegisterRecord(record);
                      }}
                    />
                  </>
                }
                {
                  activeStep === 2 &&
                  <TierInfoListCheckComponent
                    membershipRecord={membershipRecord}
                    tier={currentTier}
                    tierType={currentTier.type}
                    tierColor={currentTier.data?.color}
                    fileInfo={formFile}
                    onNext= {(record) => {
                      if (record.membership_fee === 0) {
                        submitRegisterRecord(record);
                      } else {
                        saveRegisterRecord(record);
                      }
                    }}
                  />
                }
                {
                  activeStep === 3 &&
                  <TierInfoSubmitComponent
                    membershipRecord={membershipRecord}
                    content={pageContentData}
                    fileInfo={paymentFile}
                    onUploadFile={(file) => {
                      if (file) {
                        setPaymentFile(file);
                      } else {
                        setPaymentFile(null);
                      }
                    }}
                    onNext= {(record) => {
                      submitRegisterRecord(record);
                    }}
                  />
                }
              </div>

            </div>
          </div>

          <div className="black-block full-width">
            <LogoFooterComponent />
          </div>
        </div>
      </div>
    )
  );
}